import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import type { Room } from '@studio/models/room';
import type { Organization } from '@studio/models/organization';
import { DataSTag, Sel } from '#~/stag/index.js';
import type { StudioPickerDescriptor } from '../types.js';
import {
  organizationGradientClassName,
  organizationKey,
  organizationLabel
} from './org-palette.js';
import { StudioCard } from './studio-card.jsx';
import { StudioScroller } from './studio-scroller.jsx';

export interface OrgSectionProps {
  readonly organization?: Organization | undefined;
  readonly studios: readonly StudioPickerDescriptor[];
  readonly disambiguators: ReadonlyMap<Room['id'], string>;
  readonly currentRoomId?: Room['id'] | undefined;
  readonly nudge?: boolean;
  readonly actionLabel?: 'Open' | 'Link' | undefined;
  readonly disabled?: boolean;
  readonly onSelect: (roomId: Room['id']) => void;
}

/** Organization identity and its horizontally scrollable studio row. */
export const OrgSection = ({
  organization,
  studios,
  disambiguators,
  currentRoomId,
  nudge = true,
  actionLabel,
  disabled = false,
  onSelect
}: OrgSectionProps): ReactElement => {
  const fallbackOrganizationId = studios[0]?.room.organizationId;
  const key = organizationKey(organization, fallbackOrganizationId);
  const label = organizationLabel(organization);
  const gradientClassName = organizationGradientClassName(organization, fallbackOrganizationId);
  const countLabel = `${studios.length} studio${studios.length === 1 ? '' : 's'}`;

  return (
    <section
      aria-label={`${label}, ${countLabel}`}
      className='flex flex-col gap-12'
      {...DataSTag(Sel.StudioPicker, 'organization', key)}
    >
      <header className='flex items-center gap-10 px-2'>
        <span
          className={twMerge(
            'flex h-[26px] w-[26px] shrink-0 items-center justify-center overflow-hidden rounded-6',
            'text-[12px] font-demibold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]',
            gradientClassName
          )}
          {...DataSTag(Sel.StudioPicker, 'organization_badge', key)}
        >
          {organization?.photoUrl ? (
            <img alt='' className='h-full w-full object-cover' src={organization.photoUrl} />
          ) : (
            label.charAt(0).toUpperCase()
          )}
        </span>
        <h2
          className='truncate text-[13px] font-demibold tracking-[-0.01em] text-light'
          {...DataSTag(Sel.StudioPicker, 'organization_name', key)}
        >
          {label}
        </h2>
        <span
          className='ml-auto shrink-0 whitespace-nowrap text-[11px] font-medium text-secondary'
          {...DataSTag(Sel.StudioPicker, 'organization_count', key)}
        >
          {countLabel}
        </span>
      </header>

      <StudioScroller nudge={nudge}>
        {studios.map((descriptor) => (
          <StudioCard
            key={descriptor.room.id}
            avatarGradientClassName={gradientClassName}
            descriptor={descriptor}
            disambiguator={disambiguators.get(descriptor.room.id)}
            isCurrent={currentRoomId !== undefined && descriptor.room.id === currentRoomId}
            actionLabel={actionLabel}
            disabled={disabled}
            onSelect={onSelect}
          />
        ))}
      </StudioScroller>
    </section>
  );
};
